// src/utils/sanitizeHtml.js
/**
 * Minimal HTML sanitizer for user-generated post content.
 * Strips disallowed tags/attrs but keeps inline formatting.
 * Safe enough for a controlled allow-list; NOT a general-purpose sanitizer.
 */

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL',
  'MARK', 'CODE', 'BR', 'SPAN', 'A',
  'P', 'DIV', 'H1', 'H2', 'H3', 'BLOCKQUOTE',
  'UL', 'OL', 'LI',
]);

const ALLOWED_ATTRS = {
  A: ['href', 'target', 'rel'],
  SPAN: ['style'],
  P: ['style'],
  DIV: ['style'],
  H1: ['style'],
  H2: ['style'],
  H3: ['style'],
  BLOCKQUOTE: ['style'],
  LI: ['style'],
  UL: ['style'],
  OL: ['style'],
};

const SAFE_STYLE_PROPS = new Set([
  'color', 'background-color', 'font-weight', 'font-style',
  'text-decoration', 'text-align', 'font-size', 'font-family',
]);

const safeStyle = (style) => {
  if (!style) return '';
  return style
    .split(';')
    .map((rule) => {
      const [prop, ...rest] = rule.split(':');
      const key = prop?.trim().toLowerCase();
      const val = rest.join(':').trim();
      if (!key || !val) return null;
      if (!SAFE_STYLE_PROPS.has(key)) return null;
      // Reject javascript: / expression() / url() etc.
      if (/javascript:|expression\(|url\s*\(/i.test(val)) return null;
      return `${key}: ${val}`;
    })
    .filter(Boolean)
    .join('; ');
};

export const sanitizeHtml = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined') return ''; // SSR guard

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;

  const walk = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.TEXT_NODE) continue;

      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        continue;
      }

      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Replace disallowed element with its children (unwrap)
        while (child.firstChild) node.insertBefore(child.firstChild, child);
        child.remove();
        continue;
      }

      // Strip disallowed attributes
      const allowed = ALLOWED_ATTRS[child.tagName] || [];
      for (const attr of Array.from(child.attributes)) {
        if (!allowed.includes(attr.name)) {
          child.removeAttribute(attr.name);
          continue;
        }
        if (attr.name === 'style') {
          const cleaned = safeStyle(attr.value);
          if (cleaned) child.setAttribute('style', cleaned);
          else child.removeAttribute('style');
        }
        if (attr.name === 'href') {
          const v = attr.value.trim();
          if (!/^(https?:|mailto:|\/|#)/i.test(v)) {
            child.removeAttribute('href');
          }
        }
      }

      // Force safe link defaults
      if (child.tagName === 'A') {
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }

      walk(child);
    }
  };

  walk(root);
  return root.innerHTML;
};

/** Strip all tags → plain text. Useful for `content` field + search. */
export const htmlToPlainText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined') return html.replace(/<[^>]*>/g, '');
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  return doc.body.textContent || '';
};